<template>
  <article class="resident-report-detail">
    <p v-if="loading" class="resident-report-detail__loading">Загрузка отчёта...</p>
    <p v-else-if="error" class="resident-report-detail__loading">{{ error }}</p>

    <template v-else-if="report">
      <header class="resident-report-detail__header">
        <div class="resident-report-detail__title-container">
          <a
            :href="backUrl"
            data-barba-prevent="self"
            aria-label="Вернуться к списку отчётов"
            @click="handleBackClick"
          >
            <img :src="LEFT_ARROW_ICON" alt="" aria-hidden="true" />
          </a>
          <span class="resident-report-detail__title-container--title">Отчет</span>
        </div>

        <div class="resident-report-detail__header-actions">
          <div
            class="resident-report-detail__status"
            :class="`resident-report-detail__status--${report.statusModifier}`"
          >
            <span class="resident-report-detail__status-dot"></span>
            <span class="resident-report-detail__status-text">{{ report.status }}</span>
          </div>

          <template v-if="reportActions.showSignature">
            <hr class="resident-report-detail__header-actions--divider" />

            <div
              class="resident-report-detail__signature"
              :class="
                report.isSigned
                  ? 'resident-report-detail__signature--signed'
                  : 'resident-report-detail__signature--unsigned'
              "
            >
              <img
                :src="report.isSigned ? SIGNED_ICON : NOT_SIGNED_ICON"
                alt=""
                aria-hidden="true"
              />
              <span class="resident-report-detail__signature-text">
                {{ report.isSigned ? 'Подписан' : 'Не подписан' }}
              </span>
            </div>
          </template>

          <template v-if="hasHeaderActions">
            <hr class="resident-report-detail__header-actions--divider" />

            <a-tooltip
              v-if="reportActions.canDownload"
              title="Скачать"
              placement="bottom"
              overlay-class-name="resident-report-detail__icon-action-tooltip"
            >
              <a
                class="resident-report-detail__icon-action"
                :href="report.downloadUrl"
                target="_blank"
                rel="noopener"
                aria-label="Скачать отчёт"
              >
                <img :src="DOWNLOAD_ICON" alt="" aria-hidden="true" />
              </a>
            </a-tooltip>

            <a-tooltip
              v-if="reportActions.canEdit"
              title="Редактировать"
              placement="bottom"
              overlay-class-name="resident-report-detail__icon-action-tooltip"
            >
              <button
                type="button"
                class="resident-report-detail__icon-action"
                data-report-create-open-drawer
                data-report-drawer-mode="edit"
                aria-label="Редактировать отчёт"
              >
                <img :src="EDIT_ICON" alt="" aria-hidden="true" />
              </button>
            </a-tooltip>

            <a-tooltip
              v-if="reportActions.canDelete"
              title="Удалить"
              placement="bottom"
              overlay-class-name="resident-report-detail__icon-action-tooltip"
            >
              <button
                type="button"
                class="resident-report-detail__icon-action"
                aria-label="Удалить отчёт"
                @click="handleDeleteClick"
              >
                <img :src="DELETE_ICON" alt="" aria-hidden="true" />
              </button>
            </a-tooltip>
          </template>
        </div>
      </header>

      <hr class="resident-report-detail__divider header-divider" />

      <section class="resident-report-detail__general-section">
        <span class="resident-report-detail__section-title">Общее</span>

        <div class="resident-report-detail__general-section--field-row">
          <div class="field-with-label">
            <span class="field-with-label__label">Название</span>
            <span class="field-with-label__value">{{ report.title }}</span>
          </div>
          <div class="field-with-label">
            <span class="field-with-label__label">Дата создания</span>
            <span class="field-with-label__value">{{ report.createdAt }}</span>
          </div>
        </div>

        <hr class="resident-report-detail__general-section--divider" />

        <div class="resident-report-detail__general-section--field-row">
          <div class="field-with-label">
            <span class="field-with-label__label">Организация</span>
            <span class="field-with-label__value">{{ report.companyName }}</span>
          </div>
          <div class="field-with-label">
            <span class="field-with-label__label">Отчетный период</span>
            <span class="field-with-label__value">{{ report.reportYear }}</span>
          </div>
        </div>
      </section>

      <hr class="resident-report-detail__divider" />

      <section class="resident-report-detail__metrics-section">
        <div class="resident-report-detail__metrics-section--title-container">
          <span class="resident-report-detail__metrics-section--title">Показатели</span>
          <span class="resident-report-detail__metrics-section--description">
            За отчетный выбранный период.
          </span>
        </div>

        <table class="resident-report-detail__metrics-section--table">
          <thead>
            <tr>
              <th>Показатель</th>
              <th>План</th>
              <th>Факт</th>
              <th>Отклонение, %</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="metric in report.metrics" :key="metric.label">
              <td>{{ metric.label }}</td>
              <td>{{ metric.plan }}</td>
              <td>{{ metric.fact }}</td>
              <td>{{ metric.deviation }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <hr class="resident-report-detail__divider" />

      <section class="resident-report-detail__details-section">
        <span class="resident-report-detail__section-title">Детали</span>

        <dl class="resident-report-detail__details-list">
          <div
            v-for="item in report.details"
            :key="item.label"
            class="resident-report-detail__details-item"
          >
            <span class="resident-report-detail__details-item--label">{{ item.label }}</span>
            <span class="resident-report-detail__details-item--value">{{ item.value }}</span>
          </div>
        </dl>
      </section>

      <hr class="resident-report-detail__divider" />

      <section class="resident-report-detail__section">
        <span class="resident-report-detail__section-title">Документы</span>

        <ul v-if="report.documents.length" class="resident-report-detail__documents">
          <li
            v-for="file in report.documents"
            :key="file.url + file.name"
            class="resident-report-detail__document"
          >
            <img :src="DOC_PDF_ICON" alt="" class="resident-report-detail__document-icon" />
            <div class="resident-report-detail__document-content">
              <a :href="file.url" target="_blank" rel="noopener" download>{{ file.name }}</a>
              <span>{{ file.format }} / {{ file.size }}</span>
            </div>
          </li>
        </ul>
        <p v-else class="resident-report-detail__empty-documents">Документы не загружены</p>
      </section>

      <template v-if="hasManagerReview">
        <hr class="resident-report-detail__divider" />

        <section class="resident-report-detail__section resident-report-detail__manager-review">
          <span class="resident-report-detail__section-title">Комментарий менеджера</span>

          <div class="resident-report-detail__manager-review-fields">
            <div v-if="report.commentReview" class="field-with-label">
              <span class="field-with-label__label">Комментарий</span>
              <span class="field-with-label__value">{{ report.commentReview }}</span>
            </div>

            <div v-if="report.managerFiles.length" class="field-with-label">
              <span class="field-with-label__label">Прикрепленный файл</span>
              <ul class="resident-report-detail__documents">
                <li
                  v-for="file in report.managerFiles"
                  :key="file.url + file.name"
                  class="resident-report-detail__document"
                >
                  <img :src="DOC_PDF_ICON" alt="" class="resident-report-detail__document-icon" />
                  <div class="resident-report-detail__document-content">
                    <a :href="file.url" target="_blank" rel="noopener" download>{{ file.name }}</a>
                    <span>{{ file.format }} / {{ file.size }}</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </template>
    </template>
  </article>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import {
  getReportIdFromUrl,
  getReportListHref,
  navigateToReportList,
} from './get-report-id-from-url';
import { fetchReportDetail } from '../resident-report/report-api';
import { requestDeleteReport } from '../resident-report-drawer';
import {
  getReportDetailActions,
  mapApiReportToDetailView,
  type ReportDetailViewModel,
} from './map-report-detail';

const REPORT_EDIT_DATA_ID = 'residentReportEditData';
const SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/signed.svg');
const NOT_SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/not-signed.svg');
const LEFT_ARROW_ICON = getAssetsPath('/assets/images/icons/share-icons/left-arrow.svg');
const DOWNLOAD_ICON = getAssetsPath('/assets/images/icons/share-icons/download-icon.svg');
const EDIT_ICON = getAssetsPath('/assets/images/icons/share-icons/edit-icon.svg');
const DELETE_ICON = getAssetsPath('/assets/images/icons/share-icons/delete-icon.svg');
const DOC_PDF_ICON = getAssetsPath('/assets/images/icons/doc/doc_pdf.svg');
const backUrl = getReportListHref();

function handleBackClick(event: MouseEvent): void {
  event.preventDefault();
  navigateToReportList();
}

function handleDeleteClick(): void {
  if (!report.value?.id) return;
  requestDeleteReport(report.value.id);
}

const loading = ref(true);
const error = ref('');
const report = ref<ReportDetailViewModel | null>(null);
let cleanupReportReload: (() => void) | null = null;

const reportActions = computed(() =>
  getReportDetailActions(report.value?.statusModifier ?? 'draft'),
);

const hasHeaderActions = computed(
  () =>
    reportActions.value.canDownload || reportActions.value.canEdit || reportActions.value.canDelete,
);

const hasManagerReview = computed(() => {
  if (!report.value || report.value.statusModifier !== 'revision') {
    return false;
  }

  return Boolean(report.value.commentReview) || report.value.managerFiles.length > 0;
});

onMounted(() => {
  bindReportReload();
  loadReport();
});

onBeforeUnmount(() => {
  cleanupReportReload?.();
});

watch(report, (value) => {
  syncEditPayload(value?.editPayload ?? null);
});

function syncEditPayload(payload: ReportDetailViewModel['editPayload'] | null): void {
  let script = document.getElementById(REPORT_EDIT_DATA_ID) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/json';
    script.id = REPORT_EDIT_DATA_ID;
    document.body.appendChild(script);
  }

  script.textContent = payload ? JSON.stringify(payload) : '';
}

function bindReportReload(): void {
  const reloadReport = (): void => {
    void loadReport({ silent: Boolean(report.value) });
  };

  window.addEventListener('residentReports:changed', reloadReport);
  cleanupReportReload = () => {
    window.removeEventListener('residentReports:changed', reloadReport);
    cleanupReportReload = null;
  };
}

async function loadReport(options: { silent?: boolean } = {}): Promise<void> {
  if (!options.silent) {
    loading.value = true;
  }
  error.value = '';

  const reportId = getReportIdFromUrl();
  if (!reportId) {
    error.value = 'Не удалось определить отчёт';
    loading.value = false;
    return;
  }

  try {
    const data = await fetchReportDetail(reportId);
    report.value = mapApiReportToDetailView(reportId, data);
  } catch (loadError) {
    error.value = loadError instanceof Error ? loadError.message : 'Не удалось загрузить отчёт';
    report.value = null;
  } finally {
    loading.value = false;
  }
}
</script>
